import { range } from '../utilities';
import states from '../../../../States';
import numeral from 'numeral';
import React from 'react';
import CreditorFields from '../creditor/CreditorFields';

export const VehicleFields = {
    addBy: {
        label: 'Add vehicle by',
        options: ['Year, Make, Model', 'VIN'],
    },
    annualMileage: {
        label: 'Annual Mileage',
        options: ['0 - 3,000', '3,001 - 6,000', '6,001 - 9,000', '9,001 - 12,000', '12,001 - 15,000', '15,001+'],
        required: true,
    },
    vin: {
        label: 'VIN',
        help:
            "Your vehicle's unique ID. It is most commonly located on the bottom of the window on the driver's side. It can also be found on the sticker on the driver's side door.",
        validate: (data) => data.vin !== '' || (data.year > 0 && data.make !== '' && data.model !== ''),
        required: true,
    },
    year: {
        label: 'Year',
        options: range(1900, new Date().getFullYear() + 1).reverse(),
        validate: (data) => data.vin !== '' || data.year > 0,
        required: true,
    },
    make: {
        label: 'Make',
        validate: (data) => data.vin !== '' || data.make !== '',
        required: true,
    },
    model: {
        label: 'Model',
        validate: (data) => data.vin !== '' || data.model !== '',
        required: true,
    },
    loanOrLease: {
        label: 'Do you have a Loan or Lease?',
        required: true,
    },
    garagingAddressHouseNumber: {
        label: 'Street #',
        required: true,
        validate: (data) => !data.isOtherAddress ||
            (data.garagingAddressHouseNumber !== '' || data.garagingAddressPOBox !== ''),
        type: 'number'
    },
    garagingAddressStreetName: {
        label: 'Street',
        required: true,
        validate: (data) => !data.isOtherAddress ||
            (data.garagingAddressStreetName !== '' || data.garagingAddressPOBox !== '')
    },
    garagingAddressApartmentNumber: {
        label: 'Apartment #',
        validate: () => true,
        type: 'number'
    },
    garagingAddressPOBox: {
        label: 'P.O. Box',
        validate: (data) => !data.isOtherAddress ||
        (data.garagingAddressPOBox !== '' ||
            (data.garagingAddressHouseNumber !== '' && data.garagingAddressStreetName)),
    },
    garagingAddressCity: {
        label: 'City',
        required: true,
        validate: (data) => !data.isOtherAddress || data.garagingAddressCity !== ''
    },
    garagingAddressStateId: {
        label: 'State',
        options: () => states.map((s) => s.name.toUpperCase()),
        base: 1,
        required: true,
        validate: (data) => !data.isOtherAddress || data.garagingAddressStateId !== 0
    },
    garagingAddressZip: {
        label: 'Zip (5 digits)',
        required: true,
        minimumlength: 5,
        maximumlength: 5,
        numeric: true,
		validate: (data) => !data.isOtherAddress || data.garagingAddressZip !== '',
        type: 'number'
	},
	garagingAddressCounty: {
		label: 'County',
		required: true,
		validate: (data) => !data.isOtherAddress || data.garagingAddressCounty !== ''
	},
	style: {
		label: 'Style',
		validate: (data) => data.vin !== '' || data.style !== '',
		required: true,
	},
};

export const CoverageVehicleFields = {
	comprehensive: {
		label: 'Comprehensive',
		options: [null, 50, 100, 200, 250, 500, 1000, 2000, 5000],
		help: (
			<React.Fragment>
				<p>Covers damage to your vehicle from pretty much anything that's not a collision. Think: hail, flood or the handiwork of vandals. You can adjust your Comprehensive deductible to suit your needs. Choosing a higher deductible means the cost of your insurance could be lower, but you should consider your ability to pay the deductible out of pocket if you need to file a claim.</p>
				<p>*Example: Your car is stolen. This coverage will pay up to the actual cash value of your car, minus your deductible.</p>
			</React.Fragment>
		),
	},
	collision: {
		label: 'Collision',
		options: [null, 250, 500, 1000, 2000, 5000],
		help: (
			<React.Fragment>
				<p>If you collide with something and damage your car, collision coverage pays for damage to your ride.</p>
				<p>You can adjust your Collision deductible to suit your needs. Choosing a higher deductible means the cost of your insurance could be lower, but you should consider your ability to pay the deductible out of pocket if you need to file a claim.</p>
				<p>*Example: You back into a fence post and need a new bumper. This coverage will pay to repair the damage to your vehicle minus your deductible.</p>
			</React.Fragment>
		),
	},
	rentalReimbursement: {
		label: 'Rental Reimbursement',
		options: ['$20 each day / max 30 days', '$30 each day / max 30 days', '$40 each day / max 30 days', '$50 each day / max 30 days'],
		help: <React.Fragment>Pays up to a specified amount for a rental vehicle if your car isn’t drivable because of a covered accident. i.e. 20/600 is $20 a day up to $600 maximum.</React.Fragment>,
	},
    rentalReimbursementNewco: {
		label: 'Rental Reimbursement',
		options: ['$40 per day for max 30 days', '$50 per day for max 30 days', '$75 per day for max 30 days', '$100 per day for max 30 days','$40 per day for max 60 days', '$50 per day for max 60 days','$75 per day for max 60 days', '$100 per day for max 60 days' ],
		help: <React.Fragment>Pays up to a specified amount for a rental vehicle if your car isn’t drivable because of a covered accident. i.e. 20/600 is $20 a day up to $600 maximum.</React.Fragment>,
	},
	towingAndLabor: {
		label: 'Towing and Labor',
		options: [null, 25, 50, 75, 100, 150, 200, 250, 300],
		help: <React.Fragment>We will pay towing and labor costs up to the amount you choose each time your car is disabled.</React.Fragment>,
	},
	vin: {
		label: 'VIN',
		help: "Your vehicle's unique ID. It is most commonly located on the bottom of the window on the driver's side. It can also be found on the sticker on the driver's side door.",
		required: true,
        type: 'number'
	}
};

export const AdditionalVehicleFields = {
	hasThirdPartyLoanLeaseCoverage: {
		label: 'Do you have loan / lease coverage?',
		required: true
	},
	creditorType: {
		label: 'Is the creditor a person or a company?',
		options: ['A Company (Lienholder)', 'A Person (Loss Payee)'],
		required: true
	},
	...CreditorFields,
};

export function generateDeductibleOptions(options, nullText) {
	return options.map((ded, i) => {
		return { key: i, text: ded ? `${numeral(ded).format('$0,0.00')} Deductible` : nullText, value: i };
	});
}

export function generateTowingAndLaborOptions(options, nullText) {
	return options.map((amount, i) => {
		return { key: i, text: amount ? numeral(amount).format('$0,0.00') : nullText, value: i };
	});
}
